<script>
import { fandomBasicMixin } from "src/modules/fandom_mixins_module.js";
import { get, union } from "lodash";

export default {
  mixins: [fandomBasicMixin],
  data() {
    return {
      resultContent: {},
      autocompleteSelectedContent: null,
      autocompleteContents: [],
      primaryColor: "",
      secondaryColor: "",
      primaryButtonClass: "",
      secondaryButtonClass: "",
      searchText: ""
    };
  },
  created() {
    this.setColors();
    this.getSearchableContents();
  },
  methods: {
    setColors() {
      const primaryColor = get(this, ["content", "primary_color"], "red-cabbage");
      const secondaryColor = get(this, ["content", "secondary_color"], "purple-basil");
      this.primaryColor = `var(--${primaryColor})`;
      this.secondaryColor = `var(--${secondaryColor})`;
      this.primaryButtonClass = `btn-${primaryColor}`;
      this.secondaryButtonClass = `btn-${secondaryColor}`;
    },
    autocompleteGetLabel(content) {
      return (content && content.title) || "";
    },
    autocompleteItemClicked(content) {
      if (Fandom.exists(content.slug)) {
        window.location.href = Fandom.applyContextToUrl(content.slug);
      }
    },
    autocompleteUpdateContents() {
      this.autocompleteContents = [];
    
      const successBlock = (data) => {
        const childrens = get(data, ["name_to_content", data.main_content_name, "children"], []);
        this.autocompleteContents = childrens.map(name => data.name_to_content[name]);
      };

      const params = this.getSearchableContentsParams("system-recipe-search-snippet");
      Fandom.getAutocompleteContents(params, successBlock);  
    },
    autocompleteInputChanged() {
      this.searchText = this.getSearchText();
      if (this.getSearchText().length < 3) {
        this.autocompleteContents = [];
      }
    },
    search() {
      this.getSearchableContents(false);
    },
    getSearchableContentsParams(tileLayout, excludedContents = []) {
      const limit = get(this, ["content", "limit"], 16);
      const contentFiltering = this.content.grid_contents ? [this.content.name, this.content.grid_contents] : [this.content.name];
      const params = {
        layout: tileLayout,
        layout_extra_field_name: "$empty",
        limit: parseInt(limit),
        include_rich_info: true,
        include_extra_field_content_filtering: true,
        exclude_content_ids: excludedContents,
        search_text: this.searchText,
        content_filtering: contentFiltering
      };
      if (this.resultContent) {
        params.main_content_name = this.resultContent.name;
      }
      return params;
    },
    getSearchableContents(appendContents = false) {
      const successBlock = data => {
        const mainContent = get(data, ["name_to_content", data.main_content_name], false);
        if (mainContent) {
          if (appendContents) {
            this.resultContent.children = union(this.resultContent.children, mainContent.children);
            this.resultContent.has_more_children = mainContent.has_more_children;
          } else {
            this.resultContent = mainContent;
          }

          // Workaround to prevent layout conflict with filter contents
          Object.keys(data.name_to_content).forEach(key => {
            const content = globalState.pageModel.name_to_content[key];
            if (content.name != mainContent.name) {
              Vue.set(globalState.pageModel.name_to_content, key, data.name_to_content[key]);
            }
          });
          
          Vue.nextTick(() => Fandom.emit("dom-items-loaded"));
        }
      };
      const params = this.getSearchableContentsParams(this.tileLayout, this.getExcludedContents());
      Fandom.getSearchableContents(successBlock, params, appendContents);
    },
    alternativeAppendContents() {
      this.getSearchableContents(true);
    },
    getSearchText() {
      const vaComponent = this.$refs["v-autocomplete-ref"];
      return vaComponent ? Fandom.searchTextToBackendFormat(vaComponent.searchText) : "";
    },
    setSearchText(text) {
      const vaComponent = this.$refs["v-autocomplete-ref"];
      // monkey patch on "key enter" to handle apply filters when no item is selecte on completion list
      vaComponent.keyEnter = (e) => {
        if (vaComponent.showList && vaComponent.internalItems[vaComponent.cursor]) {
          vaComponent.onSelectItem(vaComponent.internalItems[vaComponent.cursor]);
        }
        vaComponent.showList = false;
      };
      // set the autocomplete component initial search text
      vaComponent.searchText = text;
      this.searchText = text;
    },
    getExcludedContents() {
      const childrens = get(this, ["resultContent", "children"], []);
      const menuChildrens = get(this, ["menuContent", "children"], []);
      const childrenIds = [];
      childrens.concat(menuChildrens).forEach(name => {
        const id = get(globalState, ["pageModel", "name_to_content", name, "id"], -1);
        if (id > -1) {
          childrenIds.push(id);
        }
      });
      return childrenIds;
    },
  },
  computed: {
    componentStyles() {
      return {
        "--primary-color": this.primaryColor,
        "--secondary-color": this.secondaryColor
      };
    },
    searchPlaceholder() {
      const placeholder = this.ft("recipe.search_placeholder");
      return get(this, ["content", "search_placeholder"], placeholder);
    },
    tileLayout() {
      return get(this, ["content", "tile_layout"], "tile-recipe");
    },
    menuContent() {
      const categoryFilter = get(this, ["content", "category_filter"], false);
      const name = get(this, ["content", "menu"], categoryFilter);
      return Fandom.getContent(name);
    }
  }
};

</script>

<template>
  <div v-easyadmin="easyadminId" :id="`ref-${easyadminId}`" :class="contentType" class="w-100" :style="componentStyles" v-if="isContentVisibleToUser()" filters-component>
    <div class="container-fluid">
      <div class="row bg-primary py-3 justify-content-center align-items-center">
        <div class="col-12 col-md-6">
          <v-autocomplete
            ref="v-autocomplete-ref"
            class="form-control search-input p-2 d-flex align-items-center" 
            :items="autocompleteContents"
            v-model="autocompleteSelectedContent"
            :get-label="autocompleteGetLabel"
            component-item="stripe-search-autocomplete-item"
            @item-selected="autocompleteItemClicked"
            @update-items="autocompleteUpdateContents"
            @input="autocompleteInputChanged"
            :input-attrs="{ placeholder : searchPlaceholder }"
            :auto-select-one-item="false"
            ></v-autocomplete>
        </div>
        <div class="col-6 col-md-auto mt-3 mt-md-0">
          <button
            class="btn w-100 w-md-auto d-flex align-items-center justify-content-center px-3 px-md-5 py-4"
            :class="secondaryButtonClass"
            @click="search"
            >
            <i class="far fa-search mx-2"></i>
            {{ft("globals.search")}}
          </button>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="row justify-content-center mt-4" v-if="menuContent">
        <icon-menu :content="menuContent"></icon-menu>
        <div class="px-sm-0 col-12 d-flex flex-wrap">
          <h1 class="px-3 px-sm-0 mb-3 mx-2 main-content-title">{{content.title}}</h1>
        </div>
      </div>
    </div>
    <div class="container px-0">
      <breadcrumb class="px-2 pt-0"></breadcrumb>
    </div>
    <grid-fixed
      :ref="`grid-${resultContent.name}`"
      :content="resultContent"
      :key="resultContent.name"
      :alternative-append-contents="alternativeAppendContents"
      :enable-infinite-scrolling="content.enable_infinite_scrolling"
      :column-count="content.column_count"
      >
    </grid-fixed>
  </div>
</template>

<style lang="scss" scoped>
$filter-max-width: 7.35rem;

.filters-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax($filter-max-width, 1fr));
  grid-auto-rows: 1fr;

  @include media-breakpoint-up(lg) {
    grid-template-columns: repeat(auto-fit, minmax(max-content, $filter-max-width));
  }
} 
</style>
